"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { FaTags, FaHandshake, FaClock } from "react-icons/fa";
import type { StoreWithCouponsData } from "@/types/storesWithCouponsData";
import type { StoreCardData } from "@/types/store";
import type { CouponData } from "@/types/coupon";
import CouponModal from "@/components/coupon_popup";
import CouponRevealButton from "@/components/CouponRevealButton";
import { incrementCouponUsesAction } from "@/actions/couponActions"; // adjust path if needed
import renderOembed from "@/lib/iframeFunction";
import { openAffiliateLink } from "@/lib/openAffiliateLink";
import StoreEditorialContent from "@/components/StoreEditorialContent";
import { getStoreLogoAlt } from "@/lib/storeLogo";
import { AdHalfpage, AdRectangle1, AdRectangle2 } from "@/components/ads/GptAd";


/* ───────── Helpers ───────── */
function Stat({
  icon,
  value,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  onClick?: () => void;
}) {
  return (
    <div
      className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition"
      onClick={onClick}
    >
      <div className="text-purple-700 text-lg">{icon}</div>
      <div>
        <div className="text-xl font-bold text-purple-700">{value}</div>
        <div className="text-sm">{label}</div>
      </div>
    </div>
  );
}


function isExpired(coupon: CouponData, referenceDate: string) {
  if (!coupon.expirationDate) return false;
  return new Date(coupon.expirationDate).getTime() < new Date(referenceDate).getTime();
}

function extractPercent(discount?: string) {
  if (!discount) return 0;
  const match = discount.match(/(\d+)%/);
  return match ? parseInt(match[1], 10) : 0;
}

// Helper function to format discount display
function formatDiscountDisplay(discount?: string | null): {
  type: "deal" | "percentage" | "text";
  value: string;
  secondLine?: string;
} {
  if (!discount || discount.trim() === "") {
    return { type: "deal", value: "Deal" };
  }

  const discountLower = discount.toLowerCase().trim();
  
  // Check if it's a percentage or dollar amount
  if (discount.includes("%") || discount.includes("$")) {
    return { type: "percentage", value: discount };
  }

  // For text-based discounts (Free Shipping, Gift Card, etc.), split into words
  const words = discount.split(/\s+/);
  if (words.length >= 2) {
    // Split into two lines: first word(s) on top, rest on bottom
    const midPoint = Math.ceil(words.length / 2);
    return {
      type: "text",
      value: words.slice(0, midPoint).join(" "),
      secondLine: words.slice(midPoint).join(" "),
    };
  }

  // Single word - show as is
  return { type: "text", value: discount };
}

/* ───────── Component ───────── */
interface StorePageProps {
  store: StoreWithCouponsData;
  couponId: string;
  relatedStores?: StoreCardData[];
  initialActiveTab?: "all" | "promo" | "deal";
  referenceDate: string;
}

export default function StorePage({
  store,
  couponId,
  relatedStores = [],
  initialActiveTab = "all",
  referenceDate,
}: StorePageProps) {
  const [activeTab, setActiveTab] = useState<"all" | "promo" | "deal">(initialActiveTab);
  const [selectedCoupon, setSelectedCoupon] = useState<CouponData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // No pagination - show all coupons

  // Expanded coupon details
  const [expandedCoupons, setExpandedCoupons] = useState<string[]>([]);
  const toggleDetails = (couponId: string) => {
    setExpandedCoupons((prev) =>
      prev.includes(couponId) ? prev.filter((id) => id !== couponId) : [...prev, couponId]
    );
  };

  // Description expand state for mobile
  const [expandedDesc, setExpandedDesc] = useState(false);

  // Plain text description for single-line preview
  const plainDescription = store.description
    ? store.description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
    : "";

  const NEEDS_READ_MORE_THRESHOLD = 80; // characters
  const needsReadMore = plainDescription.length > NEEDS_READ_MORE_THRESHOLD;

  const coupons: CouponData[] = store.coupons || [];
  const filteredCoupons = coupons.filter((coupon) => {
    // Only show if expired or no expiration date
    const expiredOrNoDate = !coupon.expirationDate || !isExpired(coupon, referenceDate);


    if (!expiredOrNoDate) return false;

    switch (activeTab) {
      case "promo":
        return coupon.couponType === "coupon";
      case "deal":
        return coupon.couponType === "deal";
      default:
        return true;
    }
  });


  // Show all filtered coupons (no pagination)
  const paginatedCoupons = filteredCoupons;
  const expiredSectionRef = React.useRef<HTMLDivElement>(null);


  // No pagination handlers needed
  useEffect(() => {
    // Reset any state if needed when tab changes
  }, [activeTab]);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  // Auto open modal if couponId in URL
  useEffect(() => {
    if (couponId) {
      const coupon = coupons.find((c) => c._id === couponId);
      if (coupon) {
        setSelectedCoupon(coupon);
        setIsModalOpen(true);
      }
    }
  }, [couponId, coupons]);

  const handleOpenCouponNewTab = (coupon: CouponData) => {
    openAffiliateLink(coupon.couponUrl, { couponId: coupon._id });
    setSelectedCoupon(coupon);
    setIsModalOpen(true);

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("couponId", coupon._id);
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);

    }

    void incrementCouponUsesAction(coupon._id).catch((error) => {
      console.error("Failed to increment coupon uses:", error);
    });
  };
  
  const handleOpenExpiredModal = (coupon: CouponData) => {
    setSelectedCoupon(coupon);
    setIsModalOpen(true);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("couponId", coupon._id);
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);

    }

    void incrementCouponUsesAction(coupon._id).catch((error) => {
      console.error("Failed to increment coupon uses:", error);
    });
  };
  const today = new Date(referenceDate).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

  const verifiedCount = coupons.filter((c) => c.verified).length;

  return (
    <div className="w-full max-w-none px-4 md:px-8 pt-0 pb-6 text-gray-800 overflow-x-hidden">
      <p className="mb-6 border-y border-gray-200 py-4 text-center text-sm leading-6 text-gray-600 sm:text-base">
        When you buy through links on our site, we may earn a commission. Read our{" "}
        <Link href="/terms-of-use" className="underline underline-offset-2 hover:text-purple-700">
          Terms of Use
        </Link>
        .
      </p>
      <div className="flex flex-col md:flex-row gap-10 md:items-start">
        {/* Sidebar */}
        <aside className="hidden w-full space-y-8 md:order-1 md:block md:w-[26%] md:max-w-[300px] md:pt-2">
          <div className="flex justify-center">
            <AdRectangle1 />
          </div>
          <div className="text-center">
            <div className="relative mx-auto w-44 h-44 mb-6 rounded-full border-2 border-gray-300 shadow-md bg-white overflow-hidden transition hover:scale-105 flex items-center justify-center">
              <div className="relative h-[148px] w-[148px] rounded-full border border-gray-300 p-1">
                <div className="relative h-full w-full overflow-hidden rounded-full">
                <a
                  href={store.storeNetworkUrl || store.directUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit ${store.name}`}
                  className="block h-full w-full"
                >
                  <Image
                    src={store.image ? `https://www.itscoupons.com${store.image}` : "/placeholder-store.png"}
                    alt={getStoreLogoAlt(store.slug)}
                    width={140}
                    height={140}
                    sizes="140px"
                    className="h-full w-full object-cover transition-transform duration-300 hover:scale-110 hover:brightness-105"
                    style={{ width: "100%", height: "100%" }}
                  />
                </a>
                </div>
              </div>
            </div>

            <div
              className="text-sm text-gray-600 prose max-w-none text-left"
              dangerouslySetInnerHTML={{ __html: renderOembed(store.description) || "" }}
            />

            {/* Shop Button */}
            <a
              href={store.storeNetworkUrl || store.directUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full h-auto mt-3 py-2 bg-purple-600 text-white font-semibold rounded-full hover:bg-purple-700 transition"
            >
              Shop
            </a>

          </div>

          <div className="grid grid-cols-2 gap-4">
            <Stat icon={<FaTags size={28} />} value={coupons.length} label="Total Coupons" onClick={() => setActiveTab("all")} />
            <Stat
              icon={<FaHandshake size={28} />}
              value={coupons.filter((c) => c.couponType === "coupon").length}
              label="Promo Codes"
              onClick={() => setActiveTab("promo")}
            />
            <Stat
              icon={<FaHandshake size={28} />}
              value={coupons.filter((c) => c.couponType === "deal").length}
              label="Deals"
              onClick={() => setActiveTab("deal")}
            />
            <Stat
              icon={<FaClock size={28} />}
              value={coupons.filter((coupon) => isExpired(coupon, referenceDate)).length}
              label="Expired Coupons"
              onClick={() => {
                expiredSectionRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
            />

          </div>

          <StoreEditorialContent store={store} placement="sidebar" relatedStores={relatedStores} />

          <div className="flex justify-center">
            <AdHalfpage />
          </div>
        </aside>

        {/* Main Content */}
        <div className="order-1 w-full md:order-2 md:w-[74%]">
          <div className="mb-6 md:mb-8">
            <h1 className="mb-2 text-3xl font-extrabold leading-snug text-gray-900 md:text-4xl">
              {store.name} Coupon Codes
            </h1>
            <p className="text-gray-600 font-medium">
              {verifiedCount} VERIFIED OFFERS{today ? ` ON ${today}` : ""}
            </p>
          </div>

          {/* 🔹 Mobile Header */}
          <div className="block md:hidden mb-6">
            <div className="flex min-w-0 items-center gap-3">
              {/* Logo */}
              <div className="relative h-[92px] w-[92px] rounded-full border-2 border-gray-300 bg-white p-1 shadow-md overflow-hidden flex items-center justify-center shrink-0">
                <div className="relative h-full w-full rounded-full border border-gray-300 p-1">
                  <div className="relative h-full w-full rounded-full overflow-hidden">
                  <Image
                    src={store.image ? `https://www.itscoupons.com${store.image}` : "/placeholder-store.png"}
                    alt={getStoreLogoAlt(store.slug)}
                    width={80}
                    height={80}
                    sizes="80px"
                    className="h-full w-full object-cover"
                    style={{ width: "100%", height: "100%" }}
                  />
                  </div>
                </div>
              </div>
              {/* Heading + Verified line stacked */}
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="mt-2 text-gray-600 font-medium text-xs">
                  {verifiedCount} VERIFIED OFFERS{today ? ` ON ${today}` : ""}
                </p>
              </div>
            </div>
          </div>
          {store.description && (
            <div className="mt-4 mb-4 md:hidden text-sm text-gray-600 px-0">
              {!expandedDesc ? (
                <div className="flex items-baseline gap-2">
                  <span className="truncate">
                    {plainDescription}
                  </span>
                  {needsReadMore && (
                    <button
                      onClick={() => setExpandedDesc(true)}
                      className="ml-1 text-purple-700 font-medium text-xs underline whitespace-nowrap flex-shrink-0"
                      aria-expanded={expandedDesc}
                      aria-controls="store-description-mobile-top"
                    >
                      Read More
                    </button>
                  )}
                </div>
              ) : (
                <div>
                  <span
                    id="store-description-mobile-top"
                    className="contents prose text-gray-600 max-w-none [&>p:last-child]:mb-0 [&>p:last-child]:inline"
                    dangerouslySetInnerHTML={{ __html: renderOembed(store.description) }}
                  />
                  <button
                    onClick={() => setExpandedDesc(false)}
                    className="ml-2 inline-block text-purple-700 font-medium text-xs underline whitespace-nowrap"
                    aria-expanded={expandedDesc}
                    aria-controls="store-description-mobile-top"
                  >
                    Read Less
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Tabs */}
          <div className="flex gap-8 border-b text-sm font-medium mb-6">
            {(["all", "promo", "deal"] as const).map((tab) => (
              <button
                key={tab}
                className={`pb-2 ${activeTab === tab
                  ? "border-b-2 border-purple-700 text-purple-700"
                  : "hover:text-purple-600"
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "all" && `All Coupons (${coupons.length})`}
                {tab === "promo" && `Promo Codes (${coupons.filter(c => c.couponType === "coupon" && (!c.expirationDate || !isExpired(c, referenceDate))).length})`}
                {tab === "deal" && `Deals (${coupons.filter(c => c.couponType === "deal" && (!c.expirationDate || !isExpired(c, referenceDate))).length})`}
              </button>
            ))}

          </div>

          {/* Coupons List */}
          {paginatedCoupons.length > 0 && (
            <h2 className="mb-3 text-lg font-bold text-gray-900">Available {store.name} coupons and offers</h2>
          )}
          <div className="space-y-6">
            {paginatedCoupons.length === 0 && (
              <p className="text-center text-gray-500">No coupons found.</p>
            )}

            {paginatedCoupons.length > 0 && (
              <div className="flex justify-center py-1">
                <AdRectangle1 />
              </div>
            )}
            {paginatedCoupons.map((coupon, couponIndex) => {
              const percent = extractPercent(coupon.discount);
              const userSaved = (percent / 100) * 100;
              const avgSavings = (percent / 100) * 50;

              return (
                <React.Fragment key={coupon._id}>
                <div
                  className="flex flex-col border border-gray-200 rounded-xl bg-white shadow-md overflow-hidden"
                >
                  {/* Main Content Row (same as desktop) */}
                  <div className="flex flex-row items-stretch">
                    {/* Left Discount Section */}
                    <div className="flex flex-col items-center justify-center min-w-[90px] md:min-w-[120px] p-3 md:p-6 text-purple-700 font-bold">
                      {(() => {
                        const discountFormat = formatDiscountDisplay(coupon.discount);
                        if (discountFormat.type === "deal") {
                          return (
                            <span className="text-lg md:text-3xl uppercase">Deal</span>
                          );
                        } else if (discountFormat.type === "text") {
                          return (
                            <>
                              <span className="text-lg md:text-3xl uppercase">{discountFormat.value}</span>
                              {discountFormat.secondLine && (
                                <span className="text-[10px] md:text-sm uppercase">{discountFormat.secondLine}</span>
                              )}
                            </>
                          );
                        } else {
                          return (
                            <>
                              <span className="text-[10px] md:text-sm uppercase">Up To</span>
                              <span className="text-lg md:text-3xl">{discountFormat.value}</span>
                              <span className="text-[10px] md:text-sm uppercase">Off</span>
                            </>
                          );
                        }
                      })()}
                    </div>

                    {/* Middle Content */}
                    <div className="flex-1 p-3 md:p-6">
                      <div className="inline-block bg-gray-100 text-gray-700 text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded mb-2 md:mb-3">
                        {coupon.couponType === "coupon" ? "Code" : "Deal"}
                      </div>
                      <h3 className="font-semibold text-sm md:text-xl text-gray-900 mb-2 md:mb-3 line-clamp-2">
                        {coupon.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-1.5 md:gap-3 text-[10px] md:text-sm">
                        {coupon.verified && (
                          <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                            Verified
                          </span>
                        )}
                        <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          User saved ${userSaved.toFixed(2)}
                        </span>
                        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          Avg savings: ${avgSavings.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* Right Actions (desktop only) */}
                    <div className="hidden md:flex flex-col items-center justify-center min-w-[120px] md:min-w-[200px] p-3 md:p-6 border-l border-gray-100">
                      {coupon.couponType === "coupon" ? (
                        <CouponRevealButton
                          onClick={() => handleOpenCouponNewTab(coupon)}
                          aria-label={`Show code for ${coupon.title}`}
                          couponCode={coupon.couponCode}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpenCouponNewTab(coupon)}
                          className="inline-flex h-10 w-[10.9375rem] cursor-pointer items-center justify-center rounded-full bg-purple-600 px-4 text-[13px] font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] transition-colors duration-200 hover:bg-purple-700 whitespace-nowrap"
                        >
                          Get Deal
                        </button>
                      )}

                      <button
                        onClick={(e) => { e.stopPropagation(); toggleDetails(coupon._id); }}
                        className="text-xs md:text-sm text-purple-700 mt-2 md:mt-3 font-medium hover:underline"
                      >
                        {expandedCoupons.includes(coupon._id) ? "Hide Details -" : "See Details +"}
                      </button>

                      <p className="mt-1 text-[11px] md:text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium">
                        Used {coupon.uses} times
                      </p>
                    </div>
                  </div>

                  {/* Mobile Actions */}
                  <div className="flex flex-col md:hidden gap-2 p-3">
                    {coupon.couponType === "coupon" ? (
                      <CouponRevealButton
                        onClick={() => handleOpenCouponNewTab(coupon)}
                        aria-label={`Show code for ${coupon.title}`}
                        couponCode={coupon.couponCode}
                        mobile
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpenCouponNewTab(coupon)}
                        className="inline-flex h-10 w-full cursor-pointer items-center justify-center rounded-full bg-purple-600 px-4 text-[13px] font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] transition-colors duration-200 hover:bg-purple-700 whitespace-nowrap"
                      >
                        Get Deal
                      </button>
                    )}

                    <div className="flex justify-between items-center mt-2 text-[11px] text-gray-700">
                      <span
                        className="text-purple-700 font-medium hover:underline cursor-pointer"
                        onClick={(e) => { e.stopPropagation(); toggleDetails(coupon._id); }}
                      >
                        {expandedCoupons.includes(coupon._id) ? "Hide Details -" : "See Details +"}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Description */}
                  {expandedCoupons.includes(coupon._id) && coupon.description && (
                    <div className="border-t border-gray-200 p-4 bg-gray-50 text-sm text-gray-700">
                      {coupon.description}
                    </div>
                  )}
                </div>
                <div className="flex justify-center py-1">
                  {couponIndex % 2 === 0 ? <AdRectangle1 /> : <AdRectangle2 />}
                </div>
                </React.Fragment>
              );
            })}


          </div>

          {filteredCoupons.length > 0 && (
            <div className="w-full mt-8 mb-8 bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h2 className="text-xl font-bold text-gray-900">
                  {store.name} Discount Codes Currently Available –{" "}
                  {new Date(referenceDate).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    timeZone: "UTC",
                  })}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Description</th>
                      <th className="px-4 py-2 font-semibold w-28">Discount</th>
                      <th className="px-4 py-2 font-semibold w-32">End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCoupons.map((coupon) => (
                      <tr
                        key={coupon._id}
                        className="border-t hover:bg-gray-50 transition cursor-pointer"
                        onClick={() => handleOpenCouponNewTab(coupon)}
                      >
                        <td className="px-4 py-2 text-gray-800">{coupon.title}</td>
                        <td className="px-4 py-2 font-semibold text-purple-700">
                          {coupon.discount || "—"}
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {coupon.expirationDate
                            ? new Date(coupon.expirationDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              timeZone: "UTC",
                            })
                            : "Always active"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="hidden md:block">
            <StoreEditorialContent store={store} placement="main-column" />
          </div>
        </div>
      </div>

      {/* 🟥 Expired Coupons Section */}
      {coupons.some(
        (c) => isExpired(c, referenceDate)
      ) && (
          <div ref={expiredSectionRef} className="w-full max-w-7xl mx-auto mt-12 text-center">
            {/* Section Heading */}
            <h2 className="text-2xl md:text-3xl font-bold text-black mb-2">
              Expired {store.name} Coupon Codes
            </h2>
            <p className="text-black mb-6">
              Check out these expired coupons for a {store.name} discount — sometimes they still work!
            </p>

            {/* Container Box (Wide) */}
            <div className="w-full max-w-7xl mx-auto rounded-xl p-4 md:p-6 bg-gray-50 shadow-sm space-y-6 text-left">
              {coupons
                .filter(
                  (c) => isExpired(c, referenceDate)
                )
                .map((coupon) => (
                  <div
                    key={coupon._id}
                    className="max-w-4xl w-full mx-auto flex flex-col border border-black rounded-xl bg-white shadow-md overflow-hidden opacity-75"
                  >
                    {/* ─── Main Row ─── */}
                    <div className="flex flex-row items-stretch">
                      {/* Left: Discount */}
                      <div className="flex flex-col items-center justify-center min-w-[90px] md:min-w-[120px] p-3 md:p-6 text-black font-bold">
                        {(() => {
                          const discountFormat = formatDiscountDisplay(coupon.discount);
                          if (discountFormat.type === "deal") {
                            return (
                              <span className="text-lg md:text-3xl uppercase">Deal</span>
                            );
                          } else if (discountFormat.type === "text") {
                            return (
                              <>
                                <span className="text-lg md:text-3xl uppercase">{discountFormat.value}</span>
                                {discountFormat.secondLine && (
                                  <span className="text-[10px] md:text-sm uppercase">{discountFormat.secondLine}</span>
                                )}
                              </>
                            );
                          } else {
                            return (
                              <>
                                <span className="text-[10px] md:text-sm uppercase">Up To</span>
                                <span className="text-lg md:text-3xl">{discountFormat.value}</span>
                                <span className="text-[10px] md:text-sm uppercase">Off</span>
                              </>
                            );
                          }
                        })()}
                      </div>

                      {/* Middle: Content */}
                      <div className="flex-1 p-3 md:p-6">
                        <div className="inline-block bg-gray-100 text-black text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded mb-2 md:mb-3">
                          {coupon.couponType === "coupon" ? "Code" : "Deal"}
                        </div>
                        <h3 className="font-semibold text-sm md:text-xl text-black mb-2 md:mb-3 line-clamp-2">
                          {coupon.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-1.5 md:gap-3 text-[10px] md:text-sm">
                          <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">
                            Expired
                          </span>
                          {coupon.expirationDate && (
                            <span className="bg-gray-100 text-black px-1.5 py-0.5 rounded">
                              {new Date(coupon.expirationDate).toISOString().split("T")[0]}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right: Actions (Desktop) */}
                      <div className="hidden md:flex flex-col items-center justify-center min-w-[120px] md:min-w-[200px] p-3 md:p-6 border-l border-black">
                        <button type="button"
                          onClick={() => handleOpenCouponNewTab(coupon)}
                          className="w-36 h-11 bg-black hover:bg-gray-500 text-white font-semibold text-sm px-4 py-2 rounded-full"
                        >
                          Expired
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDetails(coupon._id);
                          }}
                          className="text-xs md:text-sm text-purple-700 mt-2 md:mt-3 font-medium hover:underline"
                        >
                          {expandedCoupons.includes(coupon._id)
                            ? "Hide Details -"
                            : "See Details +"}
                        </button>
                        <p className="mt-1 text-[11px] md:text-xs bg-gray-100 text-black px-2 py-0.5 rounded-full font-medium">
                          Used {coupon.uses} times
                        </p>
                      </div>
                    </div>

                    {/* ─── Mobile Actions ─── */}
                    <div className="flex flex-col md:hidden gap-2 p-3">
                      <button type="button"
                        onClick={() => handleOpenCouponNewTab(coupon)}
                        className="w-full bg-black hover:bg-gray-500 text-white font-semibold text-sm px-4 py-2 rounded-full"
                      >
                        Expired
                      </button>

                      <div className="flex justify-between items-center mt-2 text-[11px] text-black">
                        <span
                          className="text-purple-700 font-medium hover:underline"
                          onClick={() => toggleDetails(coupon._id)}
                        >
                          {expandedCoupons.includes(coupon._id)
                            ? "Hide Details -"
                            : "See Details +"}
                        </span>
                        <span className="bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                          Used {coupon.uses} times
                        </span>
                      </div>
                    </div>

                    {/* ─── Expanded Description ─── */}
                    {expandedCoupons.includes(coupon._id) && coupon.description && (
                      <div className="border-t border-black p-4 bg-gray-50 text-sm text-black">
                        {coupon.description}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

      <div className="hidden md:block">
        <StoreEditorialContent store={store} placement="faqs" />
      </div>

      <div className="hidden md:block">
        <StoreEditorialContent store={store} placement="full-content" />
      </div>



      {/* Coupon Modal */}
      <CouponModal
        storeName={store.name}
        storeSlug={store.slug}
        title={selectedCoupon?.title}
        discount={selectedCoupon?.discount}
        code={selectedCoupon?.couponCode}
        redeemUrl={selectedCoupon?.couponUrl}
        description={selectedCoupon?.description}
        storeImageUrl={store?.image}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
